import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { importAPI } from '../services/api';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
  Upload, Link2, ArrowRight, ArrowLeft, Download,
  CheckCircle, XCircle, AlertTriangle, Loader, FileSpreadsheet, RefreshCw,
  Tv, Wifi, Layers
} from 'lucide-react';

// ── System fields that can be mapped ──────────────────────────────────────────
const SYSTEM_FIELDS = [
  { key: 'name',            label: 'Customer Name',    required: true },
  { key: 'mobile',          label: 'Mobile Number',    required: true },
  { key: 'customerType',    label: 'Customer Type',    required: false, hint: 'Per-row override — leave unmapped to use batch type' },
  { key: 'address',         label: 'Address',          required: true },
  { key: 'area',            label: 'Society / Area',   required: true },
  { key: 'installationDate',label: 'Installation Date',required: false, hint: 'DD/MM/YYYY' },
  { key: 'alternateMobile', label: 'Alternate Mobile', required: false },
  { key: 'email',           label: 'Email',            required: false },
  { key: 'bbUsername',      label: 'PPPoE Username',   required: false },
  { key: 'routerModel',     label: 'Router Model',     required: false },
  { key: 'routerSerial',    label: 'Router S/N',       required: false },
  { key: 'ispName',         label: 'ISP Name',         required: false },
  { key: 'vlan',            label: 'VLAN',             required: false },
  { key: 'stbSerial',       label: 'STB Serial',       required: false },
  { key: 'stbModel',        label: 'STB Model',        required: false },
  { key: 'entityCode',      label: 'Entity Code',      required: false },
  { key: 'accountNumber',   label: 'Account Number',   required: false },
  { key: 'securityDeposit', label: 'Security Deposit', required: false },
  { key: 'notes',           label: 'Notes',            required: false },
];

const SKIP = '__skip__';
const BATCH_SIZE = 200; // rows per API call — keeps JSON well under nginx's 1 MB limit

function autoMap(headers) {
  const norm = s => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const map = {};
  const keywords = {
    name:            ['name','customername','fullname','customer'],
    mobile:          ['mobile','phone','contact','mobileno','phoneno','number'],
    customerType:    ['type','customertype','servicetype','service','plan type','connectiontype'],
    address:         ['address','addr','houseaddress','flat','houseno'],
    area:            ['area','society','locality','location','societyname'],
    installationDate:['installdate','installationdate','date','joindate','connectiondate','startdate'],
    alternateMobile: ['alternate','altmobile','secondarymobile','alt','mobile2'],
    email:           ['email','emailid','mail'],
    bbUsername:      ['pppoe','username','bbusername','broadbanduser','internetuser','pppoeuser'],
    routerModel:     ['router','routermodel','routername','modem'],
    routerSerial:    ['routersn','routerserial','modemserial','serialno','sn'],
    ispName:         ['isp','ispname','provider','serviceprovider'],
    vlan:            ['vlan','vlanid','vlan no','vlanno'],
    stbSerial:       ['stb','stbserial','settobox','stbno','sno','serialnumber','stbnumber','serial'],
    stbModel:        ['stbmodel','settoboxmodel','stbtype'],
    entityCode:      ['entitycode','entity','code'],
    accountNumber:   ['accountnumber','account','accountno','acno'],
    securityDeposit: ['deposit','security','securitydeposit','depositamount'],
    notes:           ['notes','remarks','comment','description'],
  };

  SYSTEM_FIELDS.forEach(({ key }) => {
    const found = headers.find(h => {
      const n = norm(h);
      return keywords[key]?.some(k => n.includes(norm(k)));
    });
    map[key] = found || SKIP;
  });
  return map;
}

// ── Parse file in the browser (no upload) ────────────────────────────────────
function parseFileInBrowser(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let wb;
        const isCsv = /\.csv$/i.test(file.name);
        if (isCsv) {
          let text = e.target.result;
          // Strip UTF-8 BOM (Google Sheets adds it)
          if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
          wb = XLSX.read(text, { type: 'string', cellDates: true });
        } else {
          wb = XLSX.read(e.target.result, { type: 'array', cellDates: true, raw: false });
        }
        const ws  = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        const headers = (raw[0] || []).map(h => String(h).trim()).filter(Boolean);
        const rows    = raw.slice(1).filter(r => r.some(c => String(c).trim() !== ''));
        if (headers.length === 0) return reject(new Error('File appears empty — no column headers found'));
        resolve({ headers, sampleRows: rows.slice(0, 8), allRows: rows, totalRows: rows.length });
      } catch (err) {
        reject(new Error(`Cannot parse file: ${err.message}`));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    if (/\.csv$/i.test(file.name)) {
      reader.readAsText(file, 'utf-8');
    } else {
      reader.readAsArrayBuffer(file);
    }
  });
}

export default function ImportPage() {
  const navigate = useNavigate();
  const fileRef  = useRef(null);

  const [step, setStep]         = useState(1); // 1=source 2=type 3=map 4=results
  const [source, setSource]     = useState('file'); // 'file' | 'sheet'
  const [sheetUrl, setSheetUrl] = useState('');
  const [batchType, setBatchType] = useState(null); // 'catv' | 'broadband' | 'both'
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importing, setImporting]           = useState(false);
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });

  const [preview, setPreview]   = useState(null); // { headers, sampleRows, allRows, totalRows }
  const [columnMap, setColumnMap] = useState({});  // { systemField: sheetColumnName | SKIP }
  const [results, setResults]   = useState(null);  // { success, merged, failed, errors }

  // ── Step 1: Load preview ───────────────────────────────────────────────────

  const loadFile = async (file) => {
    setLoadingPreview(true);
    try {
      // Parse entirely in browser — no file upload, no nginx size limit
      const p = await parseFileInBrowser(file);
      setPreview(p);
      setColumnMap(autoMap(p.headers));
      setStep(2);
    } catch (err) {
      toast.error(err.message || 'Could not parse file');
    } finally {
      setLoadingPreview(false);
    }
  };

  const loadSheet = async () => {
    if (!sheetUrl.trim()) { toast.error('Paste a Google Sheets URL'); return; }
    setLoadingPreview(true);
    try {
      const { data } = await importAPI.previewSheet(sheetUrl.trim());
      if (!data.success) throw new Error(data.message);
      const p = data.data;
      setPreview(p);
      setColumnMap(autoMap(p.headers));
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not fetch sheet');
    } finally {
      setLoadingPreview(false);
    }
  };

  // ── Step 3 → 4: Run import in batches ─────────────────────────────────────

  const runImport = async () => {
    const missing = SYSTEM_FIELDS.filter(f => f.required && (!columnMap[f.key] || columnMap[f.key] === SKIP));
    if (missing.length > 0) {
      toast.error(`Map required fields first: ${missing.map(f => f.label).join(', ')}`);
      return;
    }
    setImporting(true);

    const flatMap = Object.fromEntries(
      Object.entries(columnMap).map(([k, v]) => [k, v === SKIP ? null : v])
    );
    const allRows = preview.allRows;
    const headers = preview.headers;
    const totalBatches = Math.ceil(allRows.length / BATCH_SIZE);
    const combined = { success: 0, merged: 0, skipped: 0, failed: 0, errors: [] };

    setImportProgress({ done: 0, total: allRows.length });

    try {
      for (let b = 0; b < totalBatches; b++) {
        const batch = allRows.slice(b * BATCH_SIZE, (b + 1) * BATCH_SIZE);
        const { data } = await importAPI.run({
          headers,
          allRows: batch,
          columnMap: flatMap,
          batchOffset: b * BATCH_SIZE,
          batchType,
        });
        combined.success += data.data.success;
        combined.merged  += data.data.merged  || 0;
        combined.skipped += data.data.skipped || 0;
        combined.failed  += data.data.failed;
        combined.errors.push(...(data.data.errors || []));
        setImportProgress({ done: Math.min((b + 1) * BATCH_SIZE, allRows.length), total: allRows.length });
      }
      setResults(combined);
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // ── Unmapped columns count ─────────────────────────────────────────────────
  const mappedCols  = new Set(Object.values(columnMap).filter(v => v && v !== SKIP));
  const unmappedCnt = preview ? preview.headers.filter(h => !mappedCols.has(h)).length : 0;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/customers')}>
          <ArrowLeft size={14} /> Back
        </button>
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>Import Customers</h2>
          <p className="text-muted text-small" style={{ margin: 0 }}>From Google Sheets or CSV / Excel file</p>
        </div>
        <a href={importAPI.getTemplateUrl()} download style={{ marginLeft: 'auto' }}>
          <button className="btn btn-secondary btn-sm"><Download size={13} /> Template</button>
        </a>
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 28, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)' }}>
        {[
          { n: 1, label: 'Choose Source' },
          { n: 2, label: 'Customer Type' },
          { n: 3, label: 'Map Columns' },
          { n: 4, label: 'Results' },
        ].map(s => (
          <div key={s.n} style={{
            flex: 1, padding: '10px 16px', textAlign: 'center',
            background: step === s.n ? 'var(--primary)' : step > s.n ? '#d1fae5' : 'var(--card-bg)',
            color: step === s.n ? '#fff' : step > s.n ? '#065f46' : 'var(--text-muted)',
            fontWeight: step === s.n ? 700 : 500, fontSize: '0.85rem',
            borderRight: s.n < 4 ? '1px solid var(--border)' : 'none',
            transition: 'background 0.2s',
          }}>
            {step > s.n && <CheckCircle size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />}
            Step {s.n}: {s.label}
          </div>
        ))}
      </div>

      {/* ── STEP 1: Source ── */}
      {step === 1 && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
            {['file', 'sheet'].map(s => (
              <button key={s} type="button" onClick={() => setSource(s)}
                style={{
                  flex: 1, padding: '18px 20px', borderRadius: 12, cursor: 'pointer',
                  border: `2.5px solid ${source === s ? 'var(--primary)' : '#e5e7eb'}`,
                  background: source === s ? '#eff6ff' : 'var(--card-bg)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  transition: 'all 0.15s',
                }}>
                {s === 'file' ? <FileSpreadsheet size={28} color={source === s ? 'var(--primary)' : '#9ca3af'} /> : <Link2 size={28} color={source === s ? 'var(--primary)' : '#9ca3af'} />}
                <span style={{ fontWeight: 600, color: source === s ? 'var(--primary)' : 'var(--text)' }}>
                  {s === 'file' ? 'Upload File' : 'Google Sheets URL'}
                </span>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                  {s === 'file' ? 'CSV or Excel (.xlsx, .xls, .csv) — any size' : 'Paste a public share link'}
                </span>
              </button>
            ))}
          </div>

          {source === 'file' && (
            <div className="card">
              <div className="card-body" style={{ textAlign: 'center', padding: '40px 24px' }}>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
                  onChange={e => e.target.files[0] && loadFile(e.target.files[0])} />
                <div
                  onClick={() => fileRef.current?.click()}
                  style={{
                    border: '2px dashed #cbd5e1', borderRadius: 12, padding: '32px 24px',
                    cursor: 'pointer', transition: 'border-color 0.15s',
                  }}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--primary)'; }}
                  onDragLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; }}
                  onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#cbd5e1'; const f = e.dataTransfer.files[0]; if (f) loadFile(f); }}
                >
                  {loadingPreview
                    ? <><Loader size={32} color="var(--primary)" style={{ margin: '0 auto 12px', display: 'block', animation: 'spin 1s linear infinite' }} /><p>Parsing file…</p></>
                    : <><Upload size={32} color="#9ca3af" style={{ margin: '0 auto 12px', display: 'block' }} />
                        <p style={{ fontWeight: 600, marginBottom: 4 }}>Click to browse or drag & drop</p>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>CSV, XLSX or XLS — file is parsed locally, no size limit</p>
                      </>
                  }
                </div>
              </div>
            </div>
          )}

          {source === 'sheet' && (
            <div className="card">
              <div className="card-header"><h3>Google Sheets URL</h3></div>
              <div className="card-body">
                <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: '0.83rem', color: '#78350f' }}>
                  <strong>Before pasting:</strong> In Google Sheets → <em>File → Share → Share with others</em> → set to <strong>"Anyone with the link — Viewer"</strong>. Or go to <em>File → Share → Publish to web</em> and publish as CSV.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="form-control" value={sheetUrl} onChange={e => setSheetUrl(e.target.value)}
                    placeholder="https://docs.google.com/spreadsheets/d/..." style={{ flex: 1 }} />
                  <button className="btn btn-primary" onClick={loadSheet} disabled={loadingPreview}>
                    {loadingPreview ? <><Loader size={14} /> Loading…</> : <><ArrowRight size={14} /> Load</>}
                  </button>
                </div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 8 }}>
                  To import a specific tab, copy the URL while that tab is open (it includes the gid parameter automatically).
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── STEP 2: Customer Type ── */}
      {step === 2 && preview && (
        <div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setStep(1)}><ArrowLeft size={13} /> Back</button>
            <div>
              <strong>{preview.totalRows}</strong> rows loaded. What type of customers are in this file?
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
            {[
              {
                value: 'catv',
                label: 'Cable TV',
                icon: <Tv size={32} />,
                color: '#7c3aed',
                bg: '#f5f3ff',
                border: '#ddd6fe',
                desc: 'Subscribers with Set-Top Box (STB) connection. Includes entity code, account number, STB serial.',
              },
              {
                value: 'broadband',
                label: 'Broadband',
                icon: <Wifi size={32} />,
                color: '#0284c7',
                bg: '#f0f9ff',
                border: '#bae6fd',
                desc: 'Internet subscribers with PPPoE / router connection. Includes username, VLAN, ISP details.',
              },
              {
                value: 'both',
                label: 'Combo (Both)',
                icon: <Layers size={32} />,
                color: '#059669',
                bg: '#f0fdf4',
                border: '#bbf7d0',
                desc: 'Customers who have both Cable TV and Broadband. All fields from both services apply.',
              },
            ].map(opt => {
              const selected = batchType === opt.value;
              return (
                <button key={opt.value} type="button" onClick={() => setBatchType(opt.value)}
                  style={{
                    padding: '20px 16px', borderRadius: 12, cursor: 'pointer', textAlign: 'center',
                    border: `2.5px solid ${selected ? opt.color : opt.border}`,
                    background: selected ? opt.bg : 'var(--card-bg)',
                    transition: 'all 0.15s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                    boxShadow: selected ? `0 0 0 3px ${opt.border}` : 'none',
                  }}>
                  <span style={{ color: selected ? opt.color : '#9ca3af' }}>{opt.icon}</span>
                  <span style={{ fontWeight: 700, fontSize: '1rem', color: selected ? opt.color : 'var(--text)' }}>{opt.label}</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>{opt.desc}</span>
                  {selected && <CheckCircle size={16} color={opt.color} />}
                </button>
              );
            })}
          </div>

          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: '0.82rem', color: '#1e40af' }}>
            <strong>Duplicate detection:</strong> If a mobile number already exists in the system with the opposite service, the customer will automatically be upgraded to <strong>Combo</strong>. Same-type duplicates are skipped.
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="btn btn-secondary" onClick={() => setStep(1)}>Back</button>
            <button className="btn btn-primary" onClick={() => setStep(3)} disabled={!batchType}>
              Next: Map Columns <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3: Column Mapping ── */}
      {step === 3 && preview && (
        <div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setStep(2)}><ArrowLeft size={13} /> Back</button>
            <div>
              <strong>{preview.totalRows}</strong> rows · <strong>{preview.headers.length}</strong> columns ·{' '}
              <span style={{ fontWeight: 600, color: batchType === 'catv' ? '#7c3aed' : batchType === 'broadband' ? '#0284c7' : '#059669' }}>
                {batchType === 'catv' ? 'Cable TV' : batchType === 'broadband' ? 'Broadband' : 'Combo'} import
              </span>
              {unmappedCnt > 0 && <span style={{ color: '#f59e0b', marginLeft: 8 }}><AlertTriangle size={13} style={{ verticalAlign: 'middle' }} /> {unmappedCnt} columns unmapped → saved as legacy history</span>}
            </div>
          </div>

          <div className="grid-2" style={{ gap: 16 }}>
            {/* Left: mapping table */}
            <div className="card">
              <div className="card-header"><h3>Map Your Columns</h3></div>
              <div className="card-body" style={{ padding: 0 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)' }}>
                      <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)' }}>SYSTEM FIELD</th>
                      <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)' }}>YOUR COLUMN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SYSTEM_FIELDS.map(field => (
                      <tr key={field.key} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '7px 14px', fontSize: '0.83rem' }}>
                          {field.label}
                          {field.required && <span className="required" style={{ marginLeft: 3 }}>*</span>}
                          {field.hint && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{field.hint}</div>}
                        </td>
                        <td style={{ padding: '7px 14px' }}>
                          <select
                            className="form-control"
                            style={{ fontSize: '0.82rem', padding: '4px 8px' }}
                            value={columnMap[field.key] || SKIP}
                            onChange={e => setColumnMap(m => ({ ...m, [field.key]: e.target.value }))}
                          >
                            <option value={SKIP}>— skip —</option>
                            {preview.headers.map(h => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right: preview table */}
            <div className="card">
              <div className="card-header"><h3>Preview (first {preview.sampleRows.length} rows)</h3></div>
              <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', minWidth: 420 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)' }}>
                      {SYSTEM_FIELDS.filter(f => columnMap[f.key] && columnMap[f.key] !== SKIP).map(f => (
                        <th key={f.key} style={{ padding: '6px 10px', textAlign: 'left', whiteSpace: 'nowrap', color: 'var(--text-muted)', fontWeight: 600 }}>
                          {f.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sampleRows.map((row, ri) => (
                      <tr key={ri} style={{ borderTop: '1px solid var(--border)' }}>
                        {SYSTEM_FIELDS.filter(f => columnMap[f.key] && columnMap[f.key] !== SKIP).map(f => {
                          const colName = columnMap[f.key];
                          const idx = preview.headers.indexOf(colName);
                          return (
                            <td key={f.key} style={{ padding: '5px 10px', whiteSpace: 'nowrap', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {idx >= 0 ? String(row[idx] || '') : ''}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {unmappedCnt > 0 && (
                <div style={{ padding: '10px 14px', background: '#fffbeb', borderTop: '1px solid #fde68a', fontSize: '0.78rem', color: '#78350f' }}>
                  <strong>{unmappedCnt} unmapped columns</strong> will be stored as legacy history on each customer:
                  <span style={{ color: '#92400e' }}> {preview.headers.filter(h => !mappedCols.has(h)).join(', ')}</span>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18, gap: 10 }}>
            <button className="btn btn-secondary" onClick={() => setStep(2)}>Back</button>
            <button className="btn btn-primary" onClick={runImport} disabled={importing} style={{ minWidth: 200 }}>
              {importing
                ? <>
                    <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    {' '}Importing… {importProgress.done}/{importProgress.total}
                  </>
                : <><Upload size={14} /> Import {preview.totalRows} Customers</>
              }
            </button>
          </div>

          {importing && importProgress.total > 0 && (
            <div style={{ marginTop: 12, background: 'var(--card-bg)', borderRadius: 8, padding: '10px 14px', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: 6, color: 'var(--text-muted)' }}>
                <span>Importing in batches of {BATCH_SIZE}…</span>
                <span>{Math.round((importProgress.done / importProgress.total) * 100)}%</span>
              </div>
              <div style={{ background: 'var(--border)', borderRadius: 4, height: 6 }}>
                <div style={{
                  background: 'var(--primary)', borderRadius: 4, height: 6,
                  width: `${(importProgress.done / importProgress.total) * 100}%`,
                  transition: 'width 0.3s ease',
                }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── STEP 4: Results ── */}
      {step === 4 && results && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
            <div className="card" style={{ flex: 1, textAlign: 'center', borderTop: '3px solid #10b981' }}>
              <div className="card-body">
                <CheckCircle size={28} color="#10b981" style={{ margin: '0 auto 8px', display: 'block' }} />
                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#10b981' }}>{results.success}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Created</div>
              </div>
            </div>
            {results.merged > 0 && (
              <div className="card" style={{ flex: 1, textAlign: 'center', borderTop: '3px solid #7c3aed' }}>
                <div className="card-body">
                  <Layers size={28} color="#7c3aed" style={{ margin: '0 auto 8px', display: 'block' }} />
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#7c3aed' }}>{results.merged}</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Upgraded to Combo</div>
                </div>
              </div>
            )}
            {results.skipped > 0 && (
              <div className="card" style={{ flex: 1, textAlign: 'center', borderTop: '3px solid #f59e0b' }}>
                <div className="card-body">
                  <AlertTriangle size={28} color="#f59e0b" style={{ margin: '0 auto 8px', display: 'block' }} />
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: '#f59e0b' }}>{results.skipped}</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Skipped (duplicate)</div>
                </div>
              </div>
            )}
            <div className="card" style={{ flex: 1, textAlign: 'center', borderTop: `3px solid ${results.failed > 0 ? '#ef4444' : '#e5e7eb'}` }}>
              <div className="card-body">
                <XCircle size={28} color={results.failed > 0 ? '#ef4444' : '#9ca3af'} style={{ margin: '0 auto 8px', display: 'block' }} />
                <div style={{ fontSize: '2rem', fontWeight: 700, color: results.failed > 0 ? '#ef4444' : 'var(--text-muted)' }}>{results.failed}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Failed</div>
              </div>
            </div>
          </div>

          {results.errors.length > 0 && (
            <div className="card mb-4">
              <div className="card-header" style={{ background: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
                <h3 style={{ color: '#dc2626', margin: 0 }}>Skipped / Errors ({results.errors.length})</h3>
              </div>
              <div className="card-body" style={{ padding: 0, maxHeight: 320, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)' }}>
                      <th style={{ padding: '6px 14px', textAlign: 'left', width: 70 }}>Row</th>
                      <th style={{ padding: '6px 14px', textAlign: 'left' }}>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.errors.map((e, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '5px 14px', color: '#ef4444', fontWeight: 600 }}>Row {e.row}</td>
                        <td style={{ padding: '5px 14px' }}>{e.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={() => { setStep(1); setPreview(null); setResults(null); setSheetUrl(''); setBatchType(null); }}>
              <RefreshCw size={14} /> Import Another File
            </button>
            <button className="btn btn-primary" onClick={() => navigate('/customers')}>
              View Customers →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
